import {
  AfterContentInit,
  ChangeDetectorRef,
  ContentChildren,
  Directive,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  QueryList
} from '@angular/core';
import { TabsetDirectiveToken, TabsetDirectiveType } from '../tokens/tabset.directive.token';
import { TabsetGroupDirective } from './tabset-group.directive';
import { TabsetItemDirective } from './tabset-item.directive';

@Directive({
  selector: '[mbsTabset]',
  exportAs: 'mbsTabset',
  host: {
    '[class.mbs-tabset]': 'true',
    '[class.-horizontal]': 'horizontal',
    '[class.-vertical]': 'vertical',
    '[class.-inline]': 'inline'
  },
  providers: [{ provide: TabsetDirectiveToken, useExisting: forwardRef(() => TabsetDirective) }]
})
export class TabsetDirective implements AfterContentInit, TabsetDirectiveType {
  private cdRef = inject(ChangeDetectorRef);

  @Input() public activeId: string;
  @Input() loadOnAppearance = true;
  @Input() public mode: 'vertical' | 'horizontal' | 'inline' = 'horizontal';
  @Input() public defaultTab: string;

  get horizontal(): boolean {
    return this.mode === 'horizontal';
  }

  get vertical(): boolean {
    return this.mode === 'vertical';
  }

  get inline(): boolean {
    return this.mode === 'inline';
  }

  @Output() beforeUpdate = new EventEmitter();
  @Output() afterUpdate = new EventEmitter();
  @ContentChildren(TabsetItemDirective, { descendants: true }) items: QueryList<TabsetItemDirective>;
  @ContentChildren(TabsetGroupDirective, { descendants: true }) groups: QueryList<TabsetGroupDirective>;

  ngAfterContentInit(): void {
    if (this.activeId == null) {
      let nextId = this.items.first?.id || null;
      nextId = this.defaultTab && this.items.some((tab) => tab.id === this.defaultTab) ? this.defaultTab : nextId;
      this.updateActiveId(nextId, false);
      this.cdRef.detectChanges();
    }
  }

  public activateGroup(groupId: string): void {
    const group = this.groups.toArray().find((group: TabsetGroupDirective) => group.id === groupId);
    if (!group.isDisabled) {
      group.isActive = true;
    }
  }

  public click(item: TabsetItemDirective): void {
    if (!item.isDisabled) {
      this.updateActiveId(item.id);
    }
  }

  public select(itemId: string): void {
    this.updateActiveId(itemId, false);
  }

  private updateActiveId(nextId: string, emitEvent = true): void {
    if (this.activeId !== nextId) {
      let performUpdate = true;

      this.beforeUpdate.emit({
        nextId: nextId,
        currentId: this.activeId,
        stop: () => {
          performUpdate = false;
        }
      });

      if (performUpdate) {
        this.activeId = nextId;

        if (emitEvent) {
          this.afterUpdate.emit(nextId);
        }
      }
    }
  }
}
